import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type { LocationInput } from './business-info.api';

/**
 * How this store behaves: what it shows, what it alerts on, what it charges.
 *
 * The settings live in three places and the page hides that, but the split
 * matters when saving:
 *
 *   - **options** (`BusinessLocationOptionDto`) — most of them, saved whole to
 *     `businesses/options/update`;
 *   - **the location** — which departments and filters the storefront shows,
 *     saved to `businesses/{locationId}/location`;
 *   - **the business** — the order minimums and everything about layaway,
 *     saved to `businesses/{businessId}`.
 *
 * Each is only sent if it actually changed, so opening a tab and pressing Save
 * does not rewrite two records that were never touched.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `CancellationFeeType`, and the layaway fee types share the same three. */
export const FEE_KIND = { none: 0, fixed: 1, percentage: 2 } as const;

/** `IVUControlNumberIssuingAuthority`. Note the gap: there is no 2 in the UI. */
export const IVU_PROVIDERS = [0, 1, 3] as const;

/** `BusinessLocationOptionDto` — read and written whole. */
export interface StoreOptions {
  id: string;
  locationId: string | null;
  markPickedAsPacked: boolean;
  markNotPackedAsOut: boolean;
  subsSelectedByDefault: boolean;
  outOfStockTrigger: boolean;
  outOfStockCountRequirement: number;
  stripeTransferDelay: number;
  payPalTransferDelay: number;
  allowAdditionalOrders: boolean;
  hideProductsWithNoPictures: boolean;
  sendPackingFinishedAlert: boolean;
  sendPickingFinishedAlert: boolean;
  sendDelayInOrdersAlert: boolean;
  allowOrderModification: boolean;
  confirmationCodePrefix: string | null;
  showDefaultSearchFilters: boolean;
  showCustomSearchFilters: boolean;
  showHealthySearchFilters: boolean;
  showBrandSearchFilters: boolean;
  authAtOrderEntry: boolean;
  cancellationFeeType: number;
  cancellationFee: number;
  useManagedStoreBins: boolean;
  giftCardActivationFee: number | null;
}

export function useStoreOptions(locationId: string | null) {
  return useQuery({
    queryKey: ['store-options', locationId],
    queryFn: () => get<StoreOptions>(`businesses/options/${locationId}`),
    enabled: !!locationId,
  });
}

export function useUpdateStoreOptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: StoreOptions) =>
      unwrap<StoreOptions>(api.post('businesses/options/update', options)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['store-options'] }),
  });
}

/**
 * Save the location on its own.
 *
 * Keyed by the *location* id, not the business's, and it takes the same
 * `UpdateLocationDto` the business update nests — so the same complete mapping
 * applies, or the un-sent booleans come back false.
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (location: LocationInput) =>
      unwrap<unknown>(api.put(`businesses/${location.id}/location`, location)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-info'] });
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
}

// ------------------------------------------------------------------- taxes

/** `IvuCredentialDto`. */
export interface TaxCredentials {
  id: string | null;
  locationId: number;
  merchantId: string | null;
  terminalId: string | null;
  terminalPassword: string | null;
  provider: number;
}

/** `UpdateIvuCredentialsBindingObject` — no id, no location; both are implied. */
export interface TaxCredentialsInput {
  merchantId: string | null;
  terminalId: string | null;
  terminalPassword: string | null;
  provider: number;
}

export function useTaxCredentials(locationId: string | null) {
  return useQuery({
    queryKey: ['store-options', 'tax', locationId],
    queryFn: () =>
      get<TaxCredentials | null>(`businesses/taxcredentials/${locationId}`),
    enabled: !!locationId,
  });
}

export function useUpdateTaxCredentials(locationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TaxCredentialsInput) =>
      unwrap<unknown>(
        api.post(`businesses/taxcredentials/${locationId}`, input)
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['store-options', 'tax', locationId],
      }),
  });
}
